import {SupportedLocale} from '@multipass-i18n';
import {UserDecisionScreens} from '@multipass-user-service-dto';
import {immerable} from 'immer';

import {LegalDocument, Session} from '../../dal';

export type SessionStatus = 'idle' | 'awaitingCode' | 'awaitingTermsAcceptance' | 'loggingIn' | 'loggedIn' | 'loginFailed';

export type DecisionGateChild = 'updatedTerms' | 'scriptSelection';

export interface FormError {
    readonly kind: 'unknownEmail' | 'existingEmail' | 'sessionExpired';
    readonly message: string;
    readonly code: string | null;
}

export class SessionStoreModel {
    [immerable] = true;

    status: SessionStatus = 'idle';
    pendingEmail: string | null = null;
    userId: string | null = null;
    displayName: string | null = null;
    email: string | null = null;
    scriptPreference: string | null = null;
    pendingDecision: string | null = null;
    scriptError: string | null = null;
    scriptErrorCode: string | null = null;
    legalDocuments: LegalDocument[] = [];
    outstandingLegalDocumentIds: string[] = [];
    signInTicket: string | null = null;
    error: string | null = null;
    errorCode: string | null = null;
    formError: FormError | null = null;
    deleteError: string | null = null;
    deleteErrorCode: string | null = null;
    locale: SupportedLocale = 'en';

    // The blocking post-sign-in host: the server names the pending screen, the shell shows exactly
    // one child, and the app releases when it completes.
    get pendingGateChild(): DecisionGateChild | null {
        if (this.status === 'awaitingTermsAcceptance') {
            return 'updatedTerms';
        }
        if (this.status === 'loggedIn' && (this.pendingDecision === UserDecisionScreens.ScriptSelection || this.scriptPreference === null)) {
            return 'scriptSelection';
        }
        return null;
    }

    get outstandingDocuments(): LegalDocument[] {
        return this.legalDocuments.filter(document => this.outstandingLegalDocumentIds.includes(document.id));
    }

    get legalDocumentsLoaded(): boolean {
        return this.legalDocuments.length > 0;
    }

    get allLegalDocumentIds(): string[] {
        return this.legalDocuments.map(document => document.id);
    }

    onLoginLinkRequested(email: string): void {
        this.status = 'awaitingCode';
        this.pendingEmail = email;
        this.userId = null;
        this.displayName = null;
        this.email = null;
        this.error = null;
        this.errorCode = null;
        this.formError = null;
    }

    onSignUpRequested(email: string): void {
        this.status = 'awaitingCode';
        this.pendingEmail = email;
        this.userId = null;
        this.displayName = null;
        this.email = null;
        this.error = null;
        this.errorCode = null;
        this.formError = null;
    }

    onLegalDocumentsLoaded(documents: LegalDocument[]): void {
        this.legalDocuments = [...documents];
    }

    onDeleteAccountRequested(): void {
        this.deleteError = null;
        this.deleteErrorCode = null;
    }

    onDeleteAccountFailed(error: string, code: string | null): void {
        this.deleteError = error;
        this.deleteErrorCode = code;
    }

    onVerifyCodeRequested(): void {
        if (this.pendingEmail === null) {
            return;
        }
        this.status = 'loggingIn';
    }

    onAwaitingTermsRequired(outstandingLegalDocumentIds: string[], signInTicket: string): void {
        this.status = 'awaitingTermsAcceptance';
        this.outstandingLegalDocumentIds = [...outstandingLegalDocumentIds];
        this.signInTicket = signInTicket;
        this.error = null;
        this.errorCode = null;
        this.formError = null;
    }

    onSignInExpired(message: string, code: string | null): void {
        this.status = 'idle';
        this.pendingEmail = null;
        this.outstandingLegalDocumentIds = [];
        this.signInTicket = null;
        this.error = null;
        this.errorCode = null;
        this.formError = {kind: 'sessionExpired', message, code};
    }

    onLoginSuccess(session: Session): void {
        this.status = 'loggedIn';
        this.pendingEmail = null;
        this.outstandingLegalDocumentIds = [];
        this.signInTicket = null;
        this.userId = session.userId;
        this.displayName = session.displayName;
        this.email = session.email;
        this.scriptPreference = session.scriptPreference;
        this.pendingDecision = session.pendingDecision;
        this.scriptError = null;
        this.scriptErrorCode = null;
        this.error = null;
        this.errorCode = null;
        this.formError = null;
    }

    onScriptPreferenceChosen(): void {
        this.scriptError = null;
        this.scriptErrorCode = null;
    }

    onScriptPreferenceUpdated(scriptPreference: string | null, pendingDecision: string | null): void {
        this.scriptPreference = scriptPreference;
        this.pendingDecision = pendingDecision;
        this.scriptError = null;
        this.scriptErrorCode = null;
    }

    onScriptPreferenceRejected(error: string, code: string | null): void {
        this.scriptError = error;
        this.scriptErrorCode = code;
    }

    onSessionRestored(session: Session): void {
        this.onLoginSuccess(session);
    }

    onLoginError(error: string, code: string | null): void {
        if (this.status === 'loggedIn') {
            return;
        }
        this.status = 'loginFailed';
        this.error = error;
        this.errorCode = code;
        this.formError = null;
    }

    onSignInRejected(message: string, code: string): void {
        this.status = 'idle';
        this.pendingEmail = null;
        this.error = null;
        this.errorCode = null;
        this.formError = {kind: 'unknownEmail', message, code};
    }

    onSignUpRejected(message: string, code: string): void {
        this.status = 'idle';
        this.pendingEmail = null;
        this.error = null;
        this.errorCode = null;
        this.formError = {kind: 'existingEmail', message, code};
    }

    onLoginRestarted(): void {
        this.status = 'idle';
        this.pendingEmail = null;
        this.outstandingLegalDocumentIds = [];
        this.signInTicket = null;
        this.userId = null;
        this.displayName = null;
        this.email = null;
        this.scriptPreference = null;
        this.pendingDecision = null;
        this.scriptError = null;
        this.scriptErrorCode = null;
        this.error = null;
        this.errorCode = null;
        this.formError = null;
        this.deleteError = null;
        this.deleteErrorCode = null;
    }

    onSignedOut(): void {
        this.status = 'idle';
        this.pendingEmail = null;
        this.outstandingLegalDocumentIds = [];
        this.signInTicket = null;
        this.userId = null;
        this.displayName = null;
        this.email = null;
        this.scriptPreference = null;
        this.pendingDecision = null;
        this.scriptError = null;
        this.scriptErrorCode = null;
        this.error = null;
        this.errorCode = null;
        this.formError = null;
        this.deleteError = null;
        this.deleteErrorCode = null;
    }

    onLocaleChanged(locale: SupportedLocale): void {
        this.locale = locale;
    }
}
